/* wiswiz #156 — het record van een betaling die net is gestart (de Mollie-hop), bewaard vóór de hop.
   Eén leerling in de Android-app startte op 3 sep drie keer een 0,01-betaling en kwam elke keer terug op het
   app-home zonder resultaatpagina: Capacitor opent Mollie in de systeembrowser, de app werd intussen door
   Android herstart, en niets in de app wist nog dat er een betaling liep. Met dit record vraagt de app bij
   terugkomst (app weer actief, koude start, terugknop uit Mollie) de backend naar precies DIE betaling
   (`/payment/status?tr=`): betaald → de resultaatpagina; niet betaald → de checkout met één regel. */

export type BetaalRecord = {
  /* Mollie-betalings-id (tr_...), uit het antwoord van /subscription/create of /subscription/resume.
     null als een oudere backend het id nog niet meegeeft: dan valt er niets te controleren. */
  id: string | null;
  /* wanneer de hop is gezet (ms sinds epoch) */
  ts: number;
  /* pad + query van de checkout op het moment van de hop, om er precies naar terug te gaan */
  terug: string;
  plan_id: string | null;
  subscription_id?: string;
  purchase_id?: string;
};

export const BETAAL_SLEUTEL = "wiswiz_betaling_gestart";
/* Na dit venster is het geen 'net' meer: het record telt niet en wordt opgeruimd. */
export const BETAAL_VENSTER_MS = 2 * 60 * 60 * 1000;
/* Query-parameter waarmee de checkout de regel "De betaling is niet afgerond" toont. */
export const BETALING_PARAM = "betaling";
export const BETALING_NIET_AFGEROND = "niet-afgerond";
export const TERUG_MELDING = "De betaling is niet afgerond. Probeer het opnieuw.";

type Opslag = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function opslag(): Opslag | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function bewaarBetaling(rec: BetaalRecord, store: Opslag | null = opslag()): void {
  try {
    store?.setItem(BETAAL_SLEUTEL, JSON.stringify(rec));
  } catch {
    // opslag vol of geblokkeerd: de betaling gaat gewoon door, alleen de terugkeer-check ontbreekt
  }
}

export function leesBetaling(
  store: Opslag | null = opslag(),
  nu: number = Date.now()
): BetaalRecord | null {
  try {
    const raw = store?.getItem(BETAAL_SLEUTEL);
    if (!raw) return null;
    const rec = JSON.parse(raw) as Partial<BetaalRecord>;
    if (typeof rec.ts !== "number" || typeof rec.terug !== "string") {
      store?.removeItem(BETAAL_SLEUTEL);
      return null;
    }
    if (nu - rec.ts > BETAAL_VENSTER_MS || rec.ts > nu + 60_000) {
      store?.removeItem(BETAAL_SLEUTEL);
      return null;
    }
    return {
      id: typeof rec.id === "string" && rec.id ? rec.id : null,
      ts: rec.ts,
      terug: rec.terug,
      plan_id: typeof rec.plan_id === "string" ? rec.plan_id : null,
      subscription_id: typeof rec.subscription_id === "string" ? rec.subscription_id : undefined,
      purchase_id: typeof rec.purchase_id === "string" ? rec.purchase_id : undefined
    };
  } catch {
    return null;
  }
}

export function wisBetaling(store: Opslag | null = opslag()): void {
  try {
    store?.removeItem(BETAAL_SLEUTEL);
  } catch {
    // niets
  }
}

/* De checkout-URL van het record, met de parameter die de regel toont. Een record dat niet op een
   checkout wijst (kan niet, maar wees zeker) valt terug op de kale checkout. */
export function terugNaarCheckoutUrl(rec: Pick<BetaalRecord, "terug">): string {
  let pad = rec.terug.startsWith("/") ? rec.terug : "/checkout";
  if (!pad.startsWith("/checkout")) pad = "/checkout";
  const [path, query = ""] = pad.split("?");
  const qs = new URLSearchParams(query);
  qs.set(BETALING_PARAM, BETALING_NIET_AFGEROND);
  return `${path}?${qs.toString()}`;
}

/* Een betaling die afgerond is (betaald) of definitief niet (verlopen, geannuleerd, mislukt). Alles anders
   (open, pending) kan nog omslaan. */
export function isEindstatus(status: string): boolean {
  return ["paid", "expired", "canceled", "failed"].includes(status);
}
